/**
 * Where the Jot Server's `/scratchpad` is, and when this client refuses to go
 * there at all.
 *
 * ADR-0003 makes TLS mandatory: the production server is internet-facing rather
 * than sitting on a private tailnet, so the bearer token is the sole access
 * control and it travels on every request. A `http://` endpoint would hand that
 * token to the network, which is why refusing one is the client's job and not a
 * deployment note. Loopback is the single exemption, for development, and which
 * addresses count as loopback lives in `@shared/loopback` — the renderer's CSP is
 * built from that same list, because a `connect-src` and this function are the
 * same rule and the CSP is the one that actually stops the request.
 *
 * The refusal lives here, in one pure function, rather than in the two settings
 * stores: those persist what the user typed — a half-typed endpoint is not a
 * corrupt setting — and the Settings dialog explains the refusal by calling the
 * same function the network path obeys, so the rule cannot be stated twice and
 * drift.
 */

import { LOOPBACK_HOSTS } from '@shared/loopback'

export type EndpointProblem =
  /** Nothing configured. The ordinary state, not an error. */
  | 'missing'
  /** Not a URL, or one carrying credentials we would have to put on the wire. */
  | 'malformed'
  /** A URL, but not one the token may travel over. */
  | 'insecure'

export type ResolvedEndpoint =
  { ok: true; url: string } | { ok: false; problem: EndpointProblem }

/**
 * Hosts that are this machine, and the whole of the `http://` exemption. Shared
 * with the renderer's CSP rather than written out again here, which is the point
 * of `@shared/loopback` — that file records why the list is what it is, `[::1]`
 * included.
 */
const LOOPBACK: ReadonlySet<string> = new Set(LOOPBACK_HOSTS)

export const ENDPOINT_PROBLEM_MESSAGE: Record<EndpointProblem, string> = {
  missing: 'Add the address of your Jot Server.',
  malformed: 'This is not a URL Jot can use.',
  // Built from the list, so the sentence cannot promise a host the check refuses.
  insecure: `Sync needs https:// — http:// is allowed for ${LOOPBACK_HOSTS.join(' and ')} only.`,
}

/**
 * Turn a configured endpoint into the absolute URL of its `/scratchpad`.
 *
 * The endpoint is a base URL, so any path on it is kept as a prefix — a server
 * reached through a reverse proxy at `/jot` is `https://host/jot/scratchpad`.
 * A query string or fragment is dropped: neither addresses a resource, and
 * carrying them would put them on every request.
 */
export function scratchpadEndpoint(endpoint: string): ResolvedEndpoint {
  const trimmed = endpoint.trim()
  if (trimmed === '') return { ok: false, problem: 'missing' }

  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return { ok: false, problem: 'malformed' }
  }

  // Jot authenticates with a bearer token and nothing else, so credentials in
  // the URL are a second secret we would be sending without being asked to.
  if (url.username !== '' || url.password !== '') {
    return { ok: false, problem: 'malformed' }
  }

  const secure =
    url.protocol === 'https:' || (url.protocol === 'http:' && LOOPBACK.has(url.hostname))
  if (!secure) return { ok: false, problem: 'insecure' }

  const prefix = url.pathname.replace(/\/+$/, '')
  return { ok: true, url: `${url.origin}${prefix}/scratchpad` }
}
